import { SolIcon } from '@sol/icons';
import { ObjectTools } from '../../tools/object-tools';
import { SolObjectCalculator } from './sol-object-calculator';
import { SolPath } from './sol-path';
import { SolTag } from './sol-tag';
import { SolTrend } from './sol-trend';
import { SolText } from './sol-text';
import { SolValidationBuilder } from './sol-validation-builder';
import { SolValidationMessage } from './sol-validation-message';
import { SolValidator } from './sol-validator';
import { SolButton } from './sol-button';
import { SolLog } from './sol-log';
import { SolFileUpload } from './sol-file-upload';
import { SolImage } from './sol-image';
import { SolSeverity } from './sol-severity';
import { SolBudget } from './sol-budget';
import { SolDiff } from './sol-diff';

export type Kind = 'primitive' | 'object' | 'array';

export interface SolTypeExtendOptions<V> {
  validators: SolValidator<V>[];
}

export interface SolObjectTypeExtendOptions<V> extends SolTypeExtendOptions<V> {
  calculator: SolObjectCalculator<V>;
}

export class SolProperty<V> {
  constructor(
    public readonly name: string,
    public readonly type: SolType<V>,
  ) {}

  public static of<V>(name: string, type: SolType<V>): SolProperty<V> {
    return new SolProperty(name, type);
  }

  // Todo: deprecated
  public get isObject(): boolean {
    return this.type.isObject;
  }
}

/**
 * The core data type of a sol object definition. These types define primitives within the
 * application, such as boolean, text, numbers and also some specific types such as icons or tags.
 *
 * These types are used within Sol to define data structures (object definitions) and are used
 * across features, such as:
 * - forms
 * - tables (columns, filters)
 * - query fields
 *
 * These types can be used to:
 * - represent complex data objects with custom calculations
 * - validate data objects
 * - visualize and edit values, when linked to view components
 * - Serialize to/from JSON or SQL-like queries.
 *
 * @see Sol
 * @see SolForm
 * @see SolQuery
 * @see SolTableDef
 * @see SolViewComponent
 * @see ViewService
 */
export abstract class SolType<V> {
  protected constructor(
    public readonly kind: Kind,
    public readonly parent: SolType<any> | undefined,
    public readonly id: string,
    public readonly isArray: boolean,
    public readonly isObject: boolean,
    public readonly defaultValue: () => V,
    public readonly toString: (value: V | undefined) => string | undefined,
    public readonly fromString: (text: string | undefined) => V | undefined,
    public readonly validators: SolValidator<V>[] = [],
  ) {}

  //
  // Core types
  //

  public static get any(): SolObjectType<any> {
    return any;
  }

  public static get string(): SolValueType<string> {
    return string;
  }

  public static get query(): SolValueType<string> {
    return query;
  }

  public static get text(): SolValueType<SolText> {
    return text;
  }

  public static get number(): SolValueType<number> {
    return number;
  }

  public static get boolean(): SolValueType<boolean> {
    return boolean;
  }

  public static get date(): SolValueType<Date> {
    return date;
  }

  public static get icon(): SolValueType<SolIcon> {
    return icon;
  }

  public static get tag(): SolValueType<SolTag> {
    return tag;
  }

  public static get trend(): SolValueType<SolTrend> {
    return trend;
  }

  public static get severity(): SolValueType<SolSeverity> {
    return severity;
  }

  public static get button(): SolValueType<SolButton> {
    return button;
  }

  public static get log(): SolValueType<SolLog> {
    return log;
  }

  public static get budget(): SolValueType<SolBudget> {
    return budget;
  }

  public static get fileUpload(): SolValueType<SolFileUpload> {
    return fileUpload;
  }

  public static get image(): SolValueType<SolImage> {
    return image;
  }

  public static get diff(): SolValueType<SolDiff> {
    return diff;
  }

  public static get tagColor(): SolValueType<string> {
    return tagColor;
  }

  public static get relativeDate(): SolValueType<string> {
    return relativeDate;
  }

  //
  // Create / extend methods
  //

  /**
   * Creates a new (custom) primitive sol type for the given (unique!) ID and specify:
   * - The default value for this type (e.g. when adding a new form value in an array)
   * - toString: map the value to a string (used for SQL & JSON serialization)
   * - fromString: map a string to the value (used for SQL & JSON serialization)
   */
  public static ofPrimitive<V>(
    id: string,
    defaultValue: () => V,
    toString?: (value: V | undefined) => string | undefined,
    fromString?: (text: string | undefined) => V | undefined,
    parent?: SolType<any>,
  ): SolValueType<V> {
    return new SolValueType<V>(id, defaultValue, toString, fromString, [], parent);
  }

  /**
   * Create a new custom object type, for the give id (think 'class name') and a record of fields.
   */
  public static ofObject<D>(id: string, props: Record<string, SolType<any>>): SolObjectType<D> {
    return new SolObjectType<D>(id, props, [], undefined);
  }

  /**
   * Returns a type that represents an array of this type. For each primitive type this method
   * will always return the same array type object (cache).
   */
  public get array(): SolArrayType<V> {
    return new SolArrayType<V>(this);
  }

  /**
   * Extend the current type with additional configuration (e.g. calculator, validators).
   */
  public abstract extend(options: Partial<SolTypeExtendOptions<V>>): SolType<V>;

  /**
   * Returns true if the current type or one of its parents is equal to the given type.
   */
  public isA(other: SolType<any>): boolean {
    if (this === other) {
      return true;
    }

    if (this.kind === other.kind && this.id === other.id) {
      return true;
    }

    return this.parent !== undefined && this.parent.isA(other);
  }

  public validate<D>(data: D): SolValidationMessage[] {
    const result = new SolValidationBuilder();
    this.doValidate(result, data, SolPath.root);
    return result.build();
  }

  /**
   * Calculates a new value based on this type. Only object types apply calculators; other types
   * return the input as-is.
   */
  public calculate<D>(data: D): D {
    return data;
  }

  /**
   * Returns the value for the given data path.
   */
  public abstract get<D>(path: string, data: D): any | undefined;

  /**
   * Returns a copy of the given domain object, with an updated value at the given path.
   */
  public abstract set<D>(path: string, data: D, value: any): D;

  /**
   * Returns the field definition for the given data path.
   * The 'shallow' flag specified whether to return the actual field definition (leaf) or the field
   * definition that is only one level deeper within the object definition tree. This can be either
   * an ancestor or the field itself, depending on the nested structure.
   */
  public abstract getType(path: string, shallow: boolean): SolType<any> | undefined;

  /**
   * Return an array of all the data paths within the given domain object.
   * The 'first' flag will only return the first discovered path.
   */
  public abstract getPropertyPaths<D>(data: D, path: string, first: boolean): string[];

  // Todo: deprecated
  public getValPaths<D>(data: D, path: string, first: boolean): string[] {
    return this.getPropertyPaths(data, path, first);
  }

  /**
   * Serializes the value located at the given path in the provided data object to a JSON-friendly
   * structure that matches this field definition.
   */
  public abstract toJson<D>(path: string, data: D): any;

  public asValueType(): SolValueType<V> | undefined {
    return this instanceof SolValueType ? this : undefined;
  }

  public asObjectType(): SolObjectType<V> | undefined {
    return this instanceof SolObjectType ? this : undefined;
  }

  public asArrayType(): SolArrayType<V> | undefined {
    return this instanceof SolArrayType ? this : undefined;
  }

  public abstract doValidate<D>(result: SolValidationBuilder, data: D, path: string): void;
}

class SolValueType<V> extends SolType<V> {
  constructor(
    id: string,
    defaultValue: () => V,
    toString: (value: V | undefined) => string | undefined = ObjectTools.objectToString,
    fromString: (text: string | undefined) => V | undefined = ObjectTools.objectFromString,
    validators: SolValidator<V>[] = [],
    parent?: SolType<any>,
  ) {
    super(
      'primitive',
      parent ?? (id === 'any' ? undefined : SolValueType.any),
      id,
      false,
      false,
      defaultValue,
      toString,
      fromString,
      validators,
    );
  }

  public static of<V>(
    id: string,
    defaultValue: () => V,
    toString: (value: V | undefined) => string | undefined = ObjectTools.objectToString,
    fromString: (text: string | undefined) => V | undefined = ObjectTools.objectFromString,
    parent?: SolType<any>,
  ): SolValueType<V> {
    return new SolValueType<V>(id, defaultValue, toString, fromString, [], parent);
  }

  public override extend(options: Partial<SolTypeExtendOptions<V>>): SolValueType<V> {
    return new SolValueType<V>(
      this.id,
      this.defaultValue,
      this.toString,
      this.fromString,
      options.validators ?? this.validators,
    );
  }

  public get<D>(path: string, data: D): any | undefined {
    // Only root is allowed
    if (SolPath.isRoot(path)) {
      return data as any as V;
    }
    throw new Error(`Path '${path}' not found in object (SolValueType#get)`);
  }

  public set<D>(path: string, data: D, value: any): D {
    // Root path refers to the value itself
    if (SolPath.isRoot(path)) {
      return value as D;
    }

    const { name, remainder } = SolPath.pop(path);
    if (!SolPath.isRoot(remainder)) {
      throw new Error(`Path '${path}' not expected in val ${name} (SolValueType#set)`);
    }

    return {
      ...(data as any),
      [name]: value,
    };
  }

  public getType(path: string, shallow: boolean): SolType<any> | undefined {
    const { remainder } = SolPath.pop(path);
    return SolPath.isRoot(remainder) ? this : undefined;
  }

  public getPropertyPaths<D>(data: D, path: string, first: boolean): string[] {
    const targetPath = SolPath.isRoot(path) ? SolPath.root : path;
    return [targetPath];
  }

  public override doValidate<D>(result: SolValidationBuilder, data: D, path: string): void {
    for (const validator of this.validators) {
      validator.validate(result, data as any as V, path);
    }
  }

  public toJson<D>(path: string, data: D): any {
    // Root path returns the value itself
    if (SolPath.isRoot(path)) {
      return ObjectTools.serializeValue(this, data as any as V);
    }

    const { name, remainder } = SolPath.pop(path);
    if (!SolPath.isRoot(remainder)) {
      throw new Error(`Path '${path}' not found in object (expected ${name}) (SolValueType#toJson)`);
    }

    const value = (data as any)[name] as V | undefined;
    return ObjectTools.serializeValue(this, value);
  }
}

export class SolObjectType<V> extends SolType<V> {
  public readonly props: Record<string, SolType<any>>;
  private readonly propList: SolProperty<any>[];
  public readonly calculator: SolObjectCalculator<V> | undefined;

  constructor(
    id: string,
    props: Record<string, SolType<any>>,
    validators: SolValidator<V>[],
    calculator: SolObjectCalculator<V> | undefined = undefined,
  ) {
    const propList = SolObjectType.toPropList(props);
    super(
      'object',
      id === 'any' ? undefined : SolType.any,
      id,
      false,
      true,
      () => {
        const obj: any = {};
        propList.forEach(prop => (obj[prop.name] = prop.type.defaultValue()));
        return obj;
      },
      ObjectTools.objectToString,
      ObjectTools.objectFromString,
      validators,
    );

    this.props = props;
    this.propList = propList;
    this.calculator = calculator;
  }

  // Todo: deprecated
  public get fields(): SolProperty<any>[] {
    return this.propList;
  }

  public override extend(options: Partial<SolObjectTypeExtendOptions<V>>): SolObjectType<V> {
    return new SolObjectType<V>(
      this.id,
      this.props,
      options.validators ?? this.validators,
      options.calculator ?? this.calculator,
    );
  }

  public get<D>(path: string, data: D): any | undefined {
    // Root path
    const currentValue = data as any as V;
    if (SolPath.isRoot(path)) {
      return currentValue;
    }

    // Resolve property
    const { name, index, remainder } = SolPath.pop(path);
    const prop = this.findProperty(name);
    if (!prop) {
      throw new Error(`Path ${path} not found for object ${this.id} (SolObjectType#get)`);
    }

    const childValue = (data as any)[name];
    const indexPrefix = index === -1 ? '' : `[${index}]`;
    return prop.type.get(indexPrefix + remainder, childValue);
  }

  public set<D>(path: string, data: D, value: any): D {
    const currentValue = data as any as V;

    // Root path updates the entire object
    if (SolPath.isRoot(path)) {
      const calculated = this.calculateValue(value);
      return calculated as any as D;
    }

    const { name, index, remainder } = SolPath.pop(path);
    const prop = this.findProperty(name);
    if (!prop) {
      throw new Error(`Path ${path} not found for object ${this.id} (SolObjectType#set)`);
    }

    const childValue = (currentValue as any)?.[name];
    const indexPrefix = index === -1 ? '' : `[${index}]`;
    const updatedChild = prop.type.set(indexPrefix + remainder, childValue, value);
    const updatedObject = { ...(currentValue as any), [name]: updatedChild };
    const calculated = this.calculateValue(updatedObject as V);
    return calculated as any as D;
  }

  public getType(path: string, shallow: boolean = false): SolType<any> | undefined {
    if (SolPath.isRoot(path)) {
      return this;
    }

    const { name, index, remainder } = SolPath.pop(path);
    const prop = this.findProperty(name);
    if (!prop) {
      return undefined;
    }

    const indexPrefix = index === -1 ? '' : `[${index}]`;
    const candidate = prop.type.getType(indexPrefix + remainder, shallow);
    if (!candidate) {
      return undefined;
    }

    return shallow ? prop.type : candidate;
  }

  public getPropertyPaths<D>(data: D, path: string, first: boolean): string[] {
    const result: string[] = [];
    const currentValue = data as any as V;
    const currentPath = path;

    if (!SolPath.isRoot(currentPath) && !SolPath.hasIndex(currentPath)) {
      result.push(currentPath);
      if (first) {
        return result;
      }
    }

    for (const prop of this.propList) {
      const childValue = (currentValue as any)?.[prop.name];
      const childPaths = prop.type.getPropertyPaths(childValue, SolPath.go(currentPath, prop.name), first);
      if (first && childPaths.length > 0) {
        return childPaths;
      }
      result.push(...childPaths);
    }

    return result;
  }

  public override doValidate<D>(result: SolValidationBuilder, data: D, path: string): void {
    const currentValue = data as any as V;

    for (const prop of this.propList) {
      const childPath = SolPath.go(path, prop.name);
      const childValue = (currentValue as any)?.[prop.name];
      prop.type.doValidate(result, childValue, childPath);
    }

    for (const validator of this.validators) {
      validator.validate(result, currentValue, path);
    }
  }

  public toJson<D>(path: string, data: D): any {
    const currentValue = data as any as V;

    // Root path
    if (SolPath.isRoot(path)) {
      return this.toJsonFromObject(currentValue);
    }

    const { name, index, remainder } = SolPath.pop(path);
    const prop = this.findProperty(name);
    if (!prop) {
      throw new Error(`Path ${path} not found for object ${this.id} (SolObjectType#toJson)`);
    }

    const childValue = (currentValue as any)?.[name];
    const indexPrefix = index === -1 ? '' : `[${index}]`;
    return prop.type.toJson(indexPrefix + remainder, childValue);
  }

  public override calculate<D>(data: D): D {
    const baseValue = (data ?? this.defaultValue()) as any;
    let updated: any = baseValue;

    for (const prop of this.propList) {
      const childValue = baseValue?.[prop.name];
      const calculatedChild = prop.type.calculate(childValue);
      if (calculatedChild !== childValue) {
        if (updated === baseValue) {
          updated = { ...baseValue };
        }
        updated[prop.name] = calculatedChild;
      }
    }

    if (this.calculator) {
      updated = this.calculator(updated);
    }

    return updated as D;
  }

  private calculateValue(value: V): V {
    if (!this.calculator) {
      return value;
    }
    return this.calculator(value);
  }

  private findProperty(propertyName: string): SolProperty<any> | undefined {
    return this.propList.find(prop => prop.name === propertyName);
  }

  private toJsonFromObject(currentValue: any): Record<string, any> {
    const result: Record<string, any> = {};
    for (const prop of this.propList) {
      const childValue = currentValue?.[prop.name];
      const jsonValue = prop.type.toJson(SolPath.root, childValue);
      if (jsonValue !== undefined) {
        result[prop.name] = jsonValue;
      }
    }
    return result;
  }

  private static toPropList(props: Record<string, SolType<any>>): SolProperty<any>[] {
    return Object.entries(props).map(([name, type]) => SolProperty.of(name, type));
  }
}

export class SolArrayType<V> extends SolType<V[]> {
  constructor(
    public readonly itemType: SolType<V>,
    validators: SolValidator<V[]>[] = [],
  ) {
    super(
      'array',
      itemType === SolValueType.any ? SolValueType.any : SolValueType.any.array,
      itemType.id + '[]',
      true,
      itemType.isObject,
      () => [],
      v => ObjectTools.arrayToString(v, itemType),
      text => ObjectTools.stringToArray(text, itemType),
      validators,
    );
  }

  public override extend(options: Partial<SolTypeExtendOptions<V[]>>): SolArrayType<V> {
    return new SolArrayType<V>(this.itemType, options.validators ?? this.validators);
  }

  public override get<D>(path: string, data: D): any | undefined {
    const { name, index, remainder } = SolPath.pop(path);
    const values = data as any as V[];

    // Whole array
    if (index === -1) {
      if (!SolPath.isRoot(remainder)) {
        throw new Error(`Path ${path} not expected in array for field ${name} (SolArrayType#get)`);
      }
      return values;
    }

    // Bounds check
    if (!values || index < 0 || index >= values.length) {
      throw new Error(`Path ${path} out of bounds for field ${name} (SolArrayType#get)`);
    }

    // Leaf item
    if (SolPath.isRoot(remainder)) {
      return values[index];
    }

    // Delegate to item type for nested access
    return this.itemType.get(remainder, values[index]);
  }

  public override set<D>(path: string, data: D, value: any): D {
    const { name, index, remainder } = SolPath.pop(path);
    const values = data as any as V[];

    // Replace whole array
    if (index === -1) {
      if (!SolPath.isRoot(remainder)) {
        throw new Error(`Path ${path} not expected in array for field ${name} (SolArrayType#set)`);
      }
      if (Array.isArray(data) && name === '') {
        return value as any as D;
      }
      return {
        ...(data as any),
        [name]: value,
      };
    }

    // Bounds check
    if (!values || index < 0 || index >= values.length) {
      throw new Error(`Path ${path} out of bounds for field ${name} (SolArrayType#set)`);
    }

    // Update single item
    const copy = [...values];
    if (SolPath.isRoot(remainder)) {
      copy[index] = value;
    } else {
      copy[index] = this.itemType.set(remainder, copy[index], value);
    }
    if (Array.isArray(data) && name === '') {
      return copy as any as D;
    }
    return {
      ...(data as any),
      [name]: copy,
    };
  }

  public override getType(path: string, shallow: boolean): SolType<any> | undefined {
    const { index, remainder } = SolPath.pop(path);

    // Whole array reference (no index)
    if (index === -1) {
      return SolPath.isRoot(remainder) ? this : undefined;
    }

    // Index without remainder refers to the array
    if (SolPath.isRoot(remainder)) {
      return this;
    }

    const candidate = this.itemType.getType(remainder, shallow);
    if (!candidate) {
      return undefined;
    }
    return shallow ? this : candidate;
  }

  public override getPropertyPaths<D>(data: D, path: string, first: boolean): string[] {
    const values = data as any as V[];
    if (!values || values.length === 0) {
      return [];
    }

    const result: string[] = [];
    for (let i = 0; i < values.length; i++) {
      const itemPath = SolPath.at(path, i);
      if (this.itemType.isObject || this.itemType.isArray) {
        const childPaths = this.itemType.getPropertyPaths(values[i], itemPath, first);
        if (first && childPaths.length > 0) {
          return childPaths;
        }
        result.push(...childPaths);
      } else {
        if (first) {
          return [itemPath];
        }
        result.push(itemPath);
      }
    }
    return result;
  }

  public override doValidate<D>(result: SolValidationBuilder, data: D, path: string): void {
    const values = (data as any as V[]) ?? [];

    for (let i = 0; i < values.length; i++) {
      const itemPath = SolPath.at(path, i);
      this.itemType.doValidate(result, values[i], itemPath);
    }

    for (const validator of this.validators) {
      validator.validate(result, values, path);
    }
  }

  public override toJson<D>(path: string, data: D): any {
    const { name, index, remainder } = SolPath.pop(path);
    const values = data as any as V[];

    // Whole array
    if (index === -1) {
      if (!SolPath.isRoot(remainder)) {
        throw new Error(`Path ${path} not expected in array for field ${name} (SolArrayType#toJson)`);
      }
      return (values || []).map(item => this.itemType.toJson(SolPath.root, item)).filter(item => item !== undefined);
    }

    // Bounds check
    if (!values || index < 0 || index >= values.length) {
      throw new Error(`Path ${path} out of bounds for field ${name} (SolArrayType#toJson)`);
    }

    // Leaf or nested
    if (SolPath.isRoot(remainder)) {
      return this.itemType.toJson(SolPath.root, values[index]);
    }
    return this.itemType.toJson(remainder, values[index]);
  }

  public defaultItemValue(): V | undefined {
    return this.itemType.defaultValue();
  }

  public override calculate<D>(data: D): D {
    if (!Array.isArray(data)) {
      return data;
    }

    let changed = false;
    const mapped = data.map(item => {
      const calculated = this.itemType.calculate(item);
      if (calculated !== item) {
        changed = true;
      }
      return calculated;
    });

    return (changed ? mapped : data) as D;
  }
}

//
// Out-of-the-box types
//

const any = SolObjectType.ofObject<any>('any', {});
const string = SolValueType.of<string>('string', () => '', ObjectTools.identity, ObjectTools.identity);
// String subtype rendered as a read-only query preview; parent `string` keeps it a string elsewhere.
const query = SolValueType.of<string>('query', () => '', ObjectTools.identity, ObjectTools.identity, string);
const text = SolValueType.of<SolText>('text', () => SolText.empty());
const number = SolValueType.of<number>('number', () => 0, ObjectTools.numberToString, ObjectTools.numberFromString);
const boolean = SolValueType.of<boolean>(
  'boolean',
  () => false,
  ObjectTools.booleanToString,
  ObjectTools.booleanFromString,
);
const date = SolValueType.of<Date>(
  'date',
  () => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  },
  ObjectTools.dateToString,
  ObjectTools.dateFromString,
);
const icon = SolValueType.of<SolIcon>('icon', () => SolIcon.new, ObjectTools.iconToString, ObjectTools.iconFromString);
const tag = SolValueType.of<SolTag>('tag', () => SolTag.empty());
const trend = SolValueType.of<SolTrend>('trend', () => SolTrend.of(0));
const severity = SolValueType.of<SolSeverity>('severity', () => SolSeverity.of());
const button = SolValueType.of<SolButton>('button', () => SolButton.of());
const log = SolValueType.of<SolLog>('log', () => SolLog.of([]));
const budget = SolValueType.of<SolBudget>('budget', () => SolBudget.of(0, 1));
const fileUpload = SolValueType.of<SolFileUpload>('fileUpload', () => SolFileUpload.of());
const image = SolValueType.of<SolImage>('image', () => SolImage.empty());
const diff = SolValueType.of<SolDiff>('diff', () => SolDiff.empty());
const tagColor = SolValueType.of<string>(
  'tagColor',
  () => '',
  v => v ?? '',
  s => s,
  string,
);
const relativeDate = SolValueType.of<string>(
  'relativeDate',
  () => '',
  ObjectTools.identity,
  ObjectTools.identity,
  string,
);
